import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { getDeliveryList, getEdostDataNew } from "~/redux/action-creaters/cart";
import type { DeliveryApiItem } from "~/types/api/cart";

type RootState = any;

/**
 * Регион доставки (rus/spb/msk) из города и региона — та же логика, что в
 * cartNew/useCartLogic.ts. Никаких LOCATION.includes и захардкоженных городов.
 */
function getRegion(cityName: string, regionName: string): string {
  if (
    cityName === "Санкт-Петербург" ||
    regionName === "Ленинградская" ||
    regionName === "Санкт-Петербург"
  ) {
    return "spb";
  }
  if (
    cityName === "Москва" ||
    regionName === "Московская" ||
    regionName === "Москва"
  ) {
    return "msk";
  }
  return "rus";
}

/**
 * Данные и логика блока доставки на карточке товара по подходу корзины
 * (state.cart, CONFIG-модель, новые сервисы sdek/edost/shopDelivery).
 * Облегчённая версия useCartLogic: без выбора оплаты и эффектов цены.
 */
export function useProductDeliveryLogic(product: any) {
  const dispatch = useDispatch();

  const { cityName, deliveryCityData } = useSelector(
    (state: RootState) => state.location,
  );
  const { deliveryList } = useSelector((state: RootState) => state.cart);

  const [isListLoaded, setIsListLoaded] = useState(false);
  const [currentId, setCurrentId] = useState<string | null>(null);

  const regionName = deliveryCityData?.region_with_type || "";
  const currentRegion = getRegion(cityName, regionName);

  // Список доставок — тот же экшен, что использует корзина. Магазины уже в
  // сторе (state.contacts.shops, подтягиваются на SSR).
  useEffect(() => {
    let isMounted = true;
    (async () => {
      await dispatch(getDeliveryList());
      if (isMounted) {
        setIsListLoaded(true);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  const deliveryListByLocation: DeliveryApiItem[] = useMemo(() => {
    if (!Array.isArray(deliveryList)) {
      return [];
    }
    return deliveryList.filter(
      (item: DeliveryApiItem) => item.CONFIG?.LOCATION === currentRegion,
    );
  }, [deliveryList, currentRegion]);

  // Запрос данных доставки по CONFIG.MAP_LOGIC. Наличие/сроки по магазинам
  // (contacts) тянет сам ShopsNew, поэтому здесь только edost.
  useEffect(() => {
    if (!cityName || cityName === "Россия") {
      return;
    }
    if (!deliveryListByLocation.length) {
      return;
    }
    if (
      deliveryListByLocation.find((item) => item.CONFIG?.MAP_LOGIC === "edost")
    ) {
      dispatch(
        getEdostDataNew(
          cityName,
          regionName,
          deliveryCityData?.kladr_id,
          deliveryCityData?.postal_code,
          { ITEMS: [{ PRODUCT_ID: product.ID, QUANTITY: 1 }] },
        ),
      );
    }
  }, [deliveryListByLocation, cityName]);

  // Первая вкладка активна по умолчанию (как в старом компоненте).
  useEffect(() => {
    const exists = deliveryListByLocation.some((item) => item.ID === currentId);
    if (!exists && deliveryListByLocation[0]?.ID) {
      setCurrentId(deliveryListByLocation[0].ID);
    }
  }, [deliveryListByLocation]);

  const currentDelivery = useMemo(
    () => deliveryListByLocation.find((item) => item.ID === currentId),
    [deliveryListByLocation, currentId],
  );

  return {
    cityName,
    isListLoaded,
    deliveryListByLocation,
    currentId,
    setCurrentId,
    currentDelivery,
  };
}
